/* Automated Quality Response: the posture, the detection log, and the two controls.
 *
 * No X-Lumiq-Tenant header — hard rule 1.
 *
 * **Every route the contract publishes is called from here now.** The adaptation commit left three
 * unwired because the screen had no controls for them; the design has those controls, so this layer
 * carries the period configuration (`PUT /mode/observation-period`), the enablement
 * (`POST /mode/escalation`) and the summary counts (`GET /summary`).
 *
 * Two idempotency decisions, and they differ for a reason the routes state. The period is a **PUT**
 * — one value at its own address, so a retry is the same request and no key is required or wanted.
 * Enabling escalation is a **POST** that happens once, so it carries a key memoised until it
 * succeeds: a retried request after a lost response must be the same enablement, and a key that
 * never cleared would make a deliberate re-attempt a silent no-op.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  isAutonomousCapabilities,
  isAutonomousSummary,
  isDetection,
  isModeReport,
} from '@/shared/api/generated/ai-autonomous';
import type {
  AutonomousCapabilities,
  AutonomousSummary,
  Detection,
  ModeReport,
} from '@/shared/api/generated/ai-autonomous';
import {
  forgetIdempotencyKey,
  getOne,
  idempotencyKeyFor,
  post,
  put,
} from '@/shared/api/http';

export const BASE_URL = '/v1/ai/autonomous';
export const MODE_URL = `${BASE_URL}/mode`;
export const DETECTIONS_URL = `${BASE_URL}/detections`;
export const CAPABILITIES_URL = `${BASE_URL}/capabilities`;
export const SUMMARY_URL = `${BASE_URL}/summary`;
export const PERIOD_URL = `${MODE_URL}/observation-period`;
export const ESCALATION_URL = `${MODE_URL}/escalation`;

export const DETECTIONS_PER_PAGE = 25;
/** A page a person reads, well under the route's 200 maximum.
 *
 * The log is the product during observation, so the page is sized for reading rather than for
 * round trips — and the cursor means a shorter page costs nothing but a click.
 */

/** `report: null` before the first evaluation — the clock has not started.
 *
 * **`StartedMode` is gone, and the contract getting more precise is why.** It used to narrow
 * `ModeReport`'s optional timestamps to present, because the v1 contract required only `mode`. The
 * v2 contract marks every field of a started mode required and gives the not-yet-evaluated case its
 * own shape (`mode: null`, no clock), so the generated type already says what the narrowing used to
 * assert and a cast here would be the redundant assertion eslint refuses.
 */
export interface ModeState {
  readonly report: ModeReport | null;
  readonly detail: string | null;
}

function isMaybeMode(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function useCapabilities(): UseQueryResult<AutonomousCapabilities> {
  return useQuery({
    queryKey: ['autonomous', 'capabilities'],
    queryFn: () =>
      getOne(CAPABILITIES_URL, isAutonomousCapabilities, 'autonomous capabilities'),
    /* The deployment's own statement of what the engine may never do. It changes on release, not
       on evaluation, so it is cached for the session rather than refetched per view. */
    staleTime: 5 * 60 * 1000,
  });
}

export function useMode(): UseQueryResult<ModeState> {
  return useQuery({
    queryKey: ['autonomous', 'mode'],
    queryFn: async () => {
      const body = await getOne(MODE_URL, isMaybeMode, 'autonomous mode');
      /* `mode: null` is the documented pre-first-evaluation shape and is NOT a ModeReport, so it
       * cannot go through the guard — the absence is a state, not a failure. */
      if (body['mode'] === null || body['mode'] === undefined) {
        const detail = body['detail'];
        return { report: null, detail: typeof detail === 'string' ? detail : null };
      }
      /* The guard is the whole check: every field of a started mode is required in the v2 contract,
       * so `isModeReport` proves the clock is present rather than a hand-written follow-up
       * asserting it. Refusing beats rendering "Invalid Date". */
      if (!isModeReport(body)) throw new Error('mode response did not match its schema');
      return { report: body, detail: null };
    },
  });
}

export function useSummary(): UseQueryResult<AutonomousSummary> {
  return useQuery({
    queryKey: ['autonomous', 'summary'],
    queryFn: () => getOne(SUMMARY_URL, isAutonomousSummary, 'autonomous summary'),
  });
}

export interface DetectionPageState {
  readonly detections: readonly Detection[];
  readonly nextCursor: string | null;
}

export function useDetectionPage(
  cursor: string | null,
): UseQueryResult<DetectionPageState> {
  return useQuery({
    /* The cursor is in the key, so Back and Next are two cached pages rather than one refetch —
       and stepping back is instant. */
    queryKey: ['autonomous', 'detections', cursor],
    queryFn: async () => {
      const query = new URLSearchParams({ limit: String(DETECTIONS_PER_PAGE) });
      if (cursor !== null) query.set('cursor', cursor);
      const body = await getOne(
        `${DETECTIONS_URL}?${query.toString()}`,
        (
          value,
        ): value is { readonly detections: unknown; readonly next_cursor: unknown } =>
          typeof value === 'object' &&
          value !== null &&
          'detections' in value &&
          'next_cursor' in value,
        'autonomous detections',
      );
      const items: unknown = body.detections;
      if (!Array.isArray(items)) throw new Error('detections is not an array');
      /* **Refused whole rather than filtered.** The previous version dropped a malformed row with
       * `.filter(isDecision)`, and an automation's own audit log is the worst place for that: a row
       * silently missing makes a reader conclude the engine did less than it did. */
      for (const item of items) {
        if (!isDetection(item)) throw new Error('a detection did not match its schema');
      }
      const next: unknown = body.next_cursor;
      return {
        detections: items as readonly Detection[],
        /* `null` on the last page and the only honest way to say so — there is deliberately no
           total, because a keyset listing cannot know one without counting the whole table. */
        nextCursor: typeof next === 'string' && next.length > 0 ? next : null,
      };
    },
  });
}

export function useConfigureObservationPeriod(): UseMutationResult<
  ModeReport,
  Error,
  number
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (days: number) =>
      /* **A PUT with no `Idempotency-Key`, and the absence is the safety property.** The period is
       * one value at its own address, so a retry is the same request; `put` takes no key parameter,
       * which is what stops a caller sending an unkeyed POST by accident. */
      put(PERIOD_URL, isModeReport, 'observation period', { observation_days: days }),
    onSuccess: () => {
      /* The posture carries the recomputed end date and days remaining, both derived from the
         period server-side — so the card cannot show a new number beside a stale clock. */
      void client.invalidateQueries({ queryKey: ['autonomous', 'mode'] });
    },
  });
}

export function useEnableEscalation(): UseMutationResult<ModeReport, Error, void> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      post(
        ESCALATION_URL,
        /* Keyed on the tenant's single enablement and memoised until it succeeds: a retry after a
         * lost response is the same enablement, and `forgetIdempotencyKey` on success is what keeps
         * a later deliberate attempt from being a silent no-op. */
        idempotencyKeyFor('autonomous-escalation', 'enable'),
        isModeReport,
        'escalation',
        {},
      ),
    onSuccess: () => {
      forgetIdempotencyKey('autonomous-escalation', 'enable');
      /* Everything on the page moves: the posture, the tiles (which read "None while observing"),
         and every future detection's outcome. Invalidating the feature's whole key is correct
         rather than lazy — a stale tile beside an Active posture would be the screen disagreeing
         with itself about whether anybody is being paged. */
      void client.invalidateQueries({ queryKey: ['autonomous'] });
    },
  });
}
